<template>
  <div class="container py-3">
    <div class="page-header" style="margin-bottom:1.5rem;">
      <h2>{{ $t('stock.title') }}</h2>
    </div>

    <div class="table-card">
      <table class="premium-table table">
        <thead>
          <tr>
            <th>{{ $t('stock.product') }}</th>
            <th>{{ $t('stock.warehouse') }}</th>
            <th>{{ $t('stock.quantity') }}</th>
            <th>{{ $t('common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="stock.length === 0">
            <td colspan="4"><div class="empty-state"><div class="empty-icon">📦</div><p>No stock records</p></div></td>
          </tr>
          <tr v-for="item in stock" :key="`${item.product_id}-${item.warehouse_id}`">
            <td><strong>{{ item.product_name }}</strong></td>
            <td>{{ item.warehouse_name }}</td>
            <td>
              <span class="qty-badge"
                :class="item.quantity > 10 ? 'qty-ok' : item.quantity > 0 ? 'qty-low' : 'qty-zero'">
                {{ item.quantity }}
              </span>
            </td>
            <td>
              <button class="btn-action btn-action-info" @click="viewMovements(item.product_id, item.warehouse_id)">📄 {{ $t('stock.viewMovements') }}</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Stock Movements Modal -->
    <div class="modal fade" ref="movementModal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('stock.movements') }}</h5>
            <button type="button" class="btn-close" @click="hideMovementModal"></button>
          </div>
          <div class="modal-body">
            <div class="table-card-sm">
              <table class="premium-table table">
                <thead>
                  <tr>
                    <th>{{ $t('stock.date') }}</th>
                    <th>{{ $t('stock.type') }}</th>
                    <th>{{ $t('stock.quantity') }}</th>
                    <th>{{ $t('stock.reason') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="m in movements" :key="m.id">
                     <td>{{ m.created_at ? new Date(m.created_at).toLocaleString() : '—' }}</td>
                     <td>
                       <span class="status-badge"
                         :class="m.transaction_type === 'IN' || m.transaction_type === 'PURCHASE' ? 'status-received' : m.transaction_type === 'OUT' || m.transaction_type === 'SALE' ? 'status-shipped' : 'status-draft'">
                         {{ m.transaction_type }}
                       </span>
                     </td>
                     <td>
                       <span :class="m.quantity >= 0 ? 'text-success' : 'text-danger'" style="font-weight: 700">
                         {{ m.quantity >= 0 ? '+' : '' }}{{ m.quantity }}
                       </span>
                     </td>
                     <td>{{ m.reference || '—' }}</td>
                   </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination inside modal -->
            <MainPagination
              v-model:currentPage="currentMovPage"
              v-model:pageSize="movPageSize"
              :total="totalMovRecords"
            />
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="hideMovementModal">{{ $t('stock.close') }}</button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { getStockSummary, getStockMovements } from '../api/axios'
import * as bootstrap from 'bootstrap'
import MainPagination from '../components/MainPagination.vue'

const stock = ref([])
const movements = ref([])
const activeProductId = ref(null)
const activeWarehouseId = ref(null)
const currentMovPage = ref(1)
const movPageSize = ref(10)
const totalMovRecords = ref(0)

const movementModal = ref(null)
let movementModalInstance = null

const fetchStock = async () => {
  const { data } = await getStockSummary()
  stock.value = data
}

const fetchMovements = async () => {
  if (!activeProductId.value) return
  const { data } = await getStockMovements({
    product_id: activeProductId.value,
    warehouse_id: activeWarehouseId.value,
    page: currentMovPage.value,
    page_size: movPageSize.value
  })
  movements.value = data.results ?? data
  totalMovRecords.value = data.count ?? data.length
}

watch([currentMovPage, movPageSize], () => {
  fetchMovements()
})

onMounted(() => {
  fetchStock()
  movementModalInstance = new bootstrap.Modal(movementModal.value)
})

const viewMovements = async (productId, warehouseId) => {
  activeProductId.value = productId
  activeWarehouseId.value = warehouseId
  currentMovPage.value = 1
  await fetchMovements()
  movementModalInstance.show()
}

const hideMovementModal = () => {
  movementModalInstance.hide()
  activeProductId.value = null
  activeWarehouseId.value = null
}
</script>
